/**
 * v2 디자인 시스템 — 브랜드 무관 공유 foundation 토큰
 *
 * - GitLab: #2002 (팅글 리브랜딩에서 출발, 버블챗 등 공용 v2로 승격)
 * - Figma:  Tingle Design System (TL3V25ham6egfGNbwKPym6)
 *   · typography — node 124:13 (v1.0)
 *   · spacing/radius/opacity/breakpoint/layout — node 163:13 (v1.0)
 *
 * 색/그라디언트/스케일은 브랜드별이므로 여기 없음 → brand.ts / cssVars.ts 참조.
 * 특징: Pretendard · dark-first · SSR-safe.
 */

/**
 * Typography — Pretendard (Figma "Typography" node 124:13, v1.0)
 * 웹-앱 공용 4분류: Display / Title / Body / Caption.
 * - lineHeight: 배수(숫자) 또는 'normal'(Figma "lh Auto").
 * - letterSpacing: px(Display는 음수 트래킹).
 */
export const V2Font = {
  fontFamily: "'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif",
  font: {
    display: {
      large: { fontSize: '40px', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.5px' }, // 웹 히어로/랜딩
      medium: { fontSize: '32px', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.4px' }, // 웹·태블릿 히어로
      small: { fontSize: '26px', fontWeight: 700, lineHeight: 1.4, letterSpacing: '-0.28px' }, // 앱 히어로 [실측]
    },
    title: {
      large: { fontSize: '22px', fontWeight: 700, lineHeight: 1.3, letterSpacing: '0' }, // 웹 섹션 타이틀
      medium: { fontSize: '18px', fontWeight: 700, lineHeight: 'normal', letterSpacing: '0' }, // 화면 타이틀 [실측]
      small: { fontSize: '16px', fontWeight: 700, lineHeight: 'normal', letterSpacing: '0' }, // 콘텐츠·카드 제목 [실측]
    },
    body: {
      large: { fontSize: '16px', fontWeight: 400, lineHeight: 1.6, letterSpacing: '0' }, // 웹 본문
      '15': { fontSize: '15px', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0' }, // Body/15 — 바텀시트 본문 문단 [Figma 1898:170560, typography 노드 124:13 미렌더분]
      medium: { fontSize: '14px', fontWeight: 400, lineHeight: 1.6, letterSpacing: '0' }, // 웹·태블릿 본문
      mediumStrong: { fontSize: '14px', fontWeight: 700, lineHeight: 1.6, letterSpacing: '0' }, // Body/14-Bold — 프로필 이름 [Figma 4043:363710]
      small: { fontSize: '13px', fontWeight: 400, lineHeight: 1.6, letterSpacing: '0' }, // 앱 본문 [실측]
      strong: { fontSize: '13px', fontWeight: 700, lineHeight: 'normal', letterSpacing: '0' }, // 버튼·인터랙션 라벨 [실측·최다]
      xsmall: { fontSize: '12px', fontWeight: 400, lineHeight: 1.4, letterSpacing: '0' }, // 약관·해시태그 (Body/XSmall)
    },
    caption: {
      large: { fontSize: '12px', fontWeight: 700, lineHeight: 'normal', letterSpacing: '0' }, // UI 라벨
      largeRegular: { fontSize: '12px', fontWeight: 400, lineHeight: 'normal', letterSpacing: '0' }, // Caption/12(Regular) — 리스트 아이템 부제
      medium: { fontSize: '11px', fontWeight: 700, lineHeight: 'normal', letterSpacing: '0.5px' }, // 탭바 라벨 [실측]
      mediumRegular: { fontSize: '11px', fontWeight: 400, lineHeight: 'normal', letterSpacing: '0' }, // Caption/11(Regular) — 추천 카드 소개문구
      small: { fontSize: '10px', fontWeight: 700, lineHeight: 'normal', letterSpacing: '0' }, // 태그·뱃지·통계 [실측]
      metric: { fontSize: '10px', fontWeight: 500, lineHeight: 'normal', letterSpacing: '0' }, // 카운트 2만·1.1만 [실측]
      smallRegular: { fontSize: '10px', fontWeight: 400, lineHeight: 'normal', letterSpacing: '0' }, // Caption/10(Regular) — 탭바 비활성 라벨
    },
  },
} as const;

/** Spacing — Figma Variables Dimension export(spacing). 4pt 그리드 + 커스텀(6/10/28/36). */
export const V2Spacing = {
  xs: 4,
  '6': 6,
  sm: 8,
  '10': 10,
  md: 12,
  lg: 16,
  xl: 20,
  '2xl': 24,
  '28': 28,
  '3xl': 32,
  '36': 36,
  '4xl': 40,
} as const;

/** Radius — Figma Variables Dimension export(radius). */
export const V2Radius = {
  xs: 4, // 태그·뱃지
  '6': 6,
  sm: 8, // CTA 버튼·세그먼트
  md: 12, // 세그먼트 컨트롤 컨테이너
  lg: 16, // 모달
  xl: 20, // 바텀시트 상단
  '2xl': 24,
  pill: 999, // 칩(알약)
} as const;

/** Opacity — Figma Variables Opacity export. 색 토큰(border/label)의 알파는 이미 색값에 내장됨. */
export const V2Opacity = {
  '8': 0.08, // opacity/8 — border.subtle
  '12': 0.12, // opacity/12 — border.default(light)
  '20': 0.2, // opacity/20 — border.strong(light)·border.default(dark)
  disabled: 0.3, // opacity/30-disabled
  '60': 0.6, // opacity/60
  scrim: 0.8, // opacity/80-scrim — 딤 오버레이
} as const;

/**
 * Hero/배너 스크림 그라디언트 — Figma Style Guide gradients(node 57:155).
 * - banner: 배너 하단 → 페이지 배경(bg.elevated)로 자연 페이드. `var(--v2-bg-elevated)` 참조라 브랜드×모드 자동.
 * - deepDark / deepLight: 히어로 이미지 위 딥스크림(다크=어둡게, 라이트=밝게).
 *   brand.ts `heroScrim`(=`--v2-hero-scrim`) 오버라이드로 브랜드×모드가 선택. dark 미지정 시 deepDark 폴백.
 */
export const V2Scrim = {
  banner: 'linear-gradient(to bottom, rgba(25, 28, 25, 0) 45%, var(--v2-bg-elevated) 85%)', // scrim-banner (시작색=투명 elevated, Figma node 222:2942 — 순흑 보간 방지)
  deepDark: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.5), #000000)', // scrim-deep-dark
  deepLight: 'linear-gradient(to bottom, rgba(248, 248, 248, 0.5), #F8F8F8)', // scrim-deep-light
} as const;

/**
 * Breakpoints (px) — Figma Layout Rules(node 163:13)
 * 시맨틱 임계값: ≤tablet(700) 하단 내비 / ≥web(980) 상단 네비·웹 레이아웃.
 */
export const V2Breakpoint = {
  mobile: 375,
  tablet: 700,
  web: 980,
  scale: [320, 375, 430, 700, 980, 1440, 1920],
} as const;

/**
 * 반응형 미디어쿼리 헬퍼 — min-width 기준(모바일 퍼스트).
 * styled 안에서 `@media ${v2Media.desktop} { ... }` 형태로 사용.
 * (1080은 breakpoint.scale에 없는 페이지 템플릿 폭 — 4열 전환점이라 리터럴)
 */
export const v2Media = {
  sm: `screen and (min-width: 430px)`,
  tablet: `screen and (min-width: ${V2Breakpoint.tablet}px)`, // 700 → 3열, 하단 내비
  desktop: `screen and (min-width: ${V2Breakpoint.web}px)`, // 980 → 마진 120·gutter 24, 상단 네비
  lg: `screen and (min-width: 1080px)`, // 4열
  xl: `screen and (min-width: 1440px)`, // 6열
} as const;

/**
 * 헤더 높이 CSS 변수 — 구/신 UI가 갈리는 값이라 상수 대신 변수로 소비한다.
 * 값은 앱 루트에서 홈 개편 단계 롤아웃(home_renewal_percent) 판정에 따라 주입되고,
 * 미주입 환경(스토리북·다른 레이아웃 등)에서는 fallback으로 구버전 높이가 적용된다.
 */
export const V2LayoutVars = {
  navHeight: '--app-nav-height',
  mobileHeaderHeight: '--app-header-height-mobile',
} as const;

/** `var(--app-nav-height, 72px)` 형태 문자열 — styled 안에서 그대로 보간해 쓴다 */
export const v2NavHeightVar = (fallbackPx: number) => `var(${V2LayoutVars.navHeight}, ${fallbackPx}px)`;
export const v2MobileHeaderHeightVar = (fallbackPx: number) =>
  `var(${V2LayoutVars.mobileHeaderHeight}, ${fallbackPx}px)`;

/**
 * Layout 상수 — Figma Layout Rules(node 163:13, v1.0). 실측/제안값. 단위 px.
 */
export const V2Layout = {
  mobile: {
    sideMargin: 16, // spacing/lg
    contentWidth: 343, // 375 - 16*2
    sectionGap: 16, // spacing/lg [실측]
    listRowGap: 12, // spacing/md [실측]
    headerHeight: 56,
    /**
     * 페이지 상단바(대화방 제외) 높이 — **AOS 웹뷰 · iOS 웹뷰 · 모바일 브라우저 3표면 공통**.
     *
     * 전수 조사 전에는 40/52/56/60/62/64px 로 흩어져 있었고 명목 규격(40)을 지키는 화면은 28중 8곳뿐이었다.
     * 56 은 Material `?attr/actionBarSize`(56dp)·웹 v2 모바일 헤더 토큰과 일치하고,
     * 44pt/48dp 터치 타깃을 모두 담는다(40 은 둘 다 불가 — 네이티브 toolbar_default 가 40dp 컨테이너에
     * 44dp 버튼을 넣어 스스로 클립하고 있다).
     *
     * 상태바·safe area·브라우저 주소창은 이 값에 포함하지 않는다 — 상단 크롬은 표면마다 24/44/72 로 다르고
     * 바 본체만 공통이다.
     */
    pageTopBarHeight: 56,
    /** #2007 홈 개편 시안의 모바일 헤더. Nav Bar 인스턴스(833:92687) 실측: Actions(h52)가 y=10 → 10+52+10 */
    headerHeightRenewal: 72,
    /** 홈 인디케이터 포함 (앱 네이티브 가이드 화면). 60 + 하단 28.
     *  타 서비스 대비 하단 여백이 과하다는 판단으로 40 → 28로 조정(2026-08-13 확정, 앱·PWA 공통). */
    tabBarHeight: 88,
    // 웹(PWA) 모바일뷰 하단 내비 — 64 + iOS 세이프에어리어 34 (앱 tabBarHeight 88과 구분, Figma 166:127~129)
    webBottomNav: { height: 64, safeArea: 34 },
    ctaBarHeight: 100, // [실측]
    cardGrid: { columns: 2, cardWidth: 165, gutter: 13, gutterFluid: 12 }, // gutter=375 고정 실측 / gutterFluid=가변폭(375 외) spacing/md
  },
  web: {
    // ≥980, 상단 네비
    sideMargin: 120, // 1440/1080/980 공통
    // Figma(186:9546/223:9890)는 84이지만 좌측 사이드 메뉴 구조에서는 그만큼 필요 없어 72로 고정
    // (2026-08-27 997d18cd3e). --app-nav-height를 세팅하는 곳이 없어 이 값이 실효 높이 — 84로 되돌리지 말 것.
    navHeight: 72,
    /**
     * #2007 홈 개편 시안의 데스크톱 헤더. Guide Home instance(349:31063) 실측: Frame 56(h52)이 y=16 → 16+52+16.
     * #2007 홈 시안(833:90671)도 동일. (과거 주석은 349:31063을 72로 적었으나 실제 노드는 84 — figmaMap이 맞다)
     */
    navHeightRenewal: 84,
    /**
     * #2007 홈 개편 시안(833:90669) 실측 — 1440에서 바 1200(= 좌우 sideMargin 120), 콘텐츠는 그 안쪽 좌우 16을
     * 뺀 1168(카드 184.67 × 6 + gutter 12 × 5). 구 홈은 1024 그대로라 값을 갈라둔다.
     */
    contentWidthRenewal: 1168,
    /** 바 최대 폭 — 콘텐츠 1168 + 좌우 인셋 16. `v2WebBarMaxWidth`의 상한이다. */
    barWidthRenewal: 1200,
    /** 좌우 여백이 사라지기 시작하는 바 폭 — 980 시안의 바(= 980 - sideMargin 120 × 2) */
    barMinWidthRenewal: 740,
    sectionGap: 28,
    gridGutter: 24, // spacing/2xl
    // 컬럼 수: 1440→6 · 1080→4 · 980→3 (카드 폭 가변)
  },
  control: {
    ctaHeight: 44, // 풀폭 343×44, radius/sm
    minTouchTarget: 44, // 접근성
    tag: { height: 18, radius: 4, paddingX: 6 }, // Caption/Small
  },
  modal: {
    width: 320, // Figma Alert/Dialogue 심볼 폭(340:610/616) — 중앙 정렬 카드
    radius: 16, // radius/lg
    padding: 20, // spacing/xl
    actionHeight: 44,
    actionGap: 9, // Figma Two-button 컨테이너 gap(1084:96146, 비그리드값)
  },
  bottomSheet: {
    topRadius: 20, // radius/xl
    handle: { width: 36, height: 4, top: 8 },
    padding: { x: 16, top: 20, bottom: 34 }, // 세이프에어리어 포함
  },
} as const;

/**
 * #2007 시안의 웹 "바" 폭 규칙 — styled의 `max-width` 값으로 그대로 보간해 쓴다.
 * 헤더(V2NavBar inner)와 본문 컨테이너가 **같은 축**을 쓰도록 한 곳에 모아둔 값이다.
 *
 * 시안 실측(Guide/Home 833:90669 · 833:90722 · 833:90870):
 *   1440 → 바 1200 (좌우 sideMargin 120) · 980 → 바 740 (좌우 120) · 430 → 바 430 (여백 0)
 *
 * - `100% - 240px` — 좌우 sideMargin 120을 확보한다. 이게 없으면 980에서 바가 화면을 꽉 채워
 *   카드가 시안(168)보다 58px 커진다(실측 226.5).
 * - `min(1200px, …)` — 1440을 넘어도 바는 1200에서 멈춘다(넓은 화면에서 카드가 무한정 커지지 않게).
 * - `max(740px, …)` — 980 아래로 내려갈 때 740에서 멈춘다. 여기서 멈추지 않으면 700대에서
 *   여백만 남고 콘텐츠가 과하게 좁아진다. 740 미만 뷰포트에선 max-width가 걸리지 않아 100%가 되고,
 *   모바일 시안(430 → 바 430, 여백 0)으로 자연스럽게 이어진다.
 *
 * 계단식 미디어쿼리 대신 한 줄로 두는 이유 — 980 경계에서 카드 폭이 168↔227로 튀지 않는다.
 *
 * ⚠ `100%`는 **부모 폭** 기준이라 이 규칙을 이미 적용한 컨테이너 **안에서 또 쓰면 안 된다**
 *   (부모가 이미 1200이면 자식은 960으로 줄어든다). 자식은 `max-width: barWidthRenewal`만 두고
 *   부모 폭을 그대로 채우게 한다.
 */
export const v2WebBarMaxWidth = `min(${V2Layout.web.barWidthRenewal}px, max(${V2Layout.web.barMinWidthRenewal}px, 100% - ${V2Layout.web.sideMargin * 2}px))`;

export type V2FontType = typeof V2Font;
export type V2LayoutType = typeof V2Layout;
